<script lang="ts">
	import { DataHandlerScene } from "luxedo-data"
	import SceneUploadUpload from "./SceneUpload_Upload.svelte"
	import { LuxedoRPC, RPCError, waitForUploadReady } from "luxedo-rpc"
	import SceneUploadProcess from "./SceneUpload_Process.svelte"
	import { DirectUploadProgressContext } from "../../../../../../modules/progress-reporting/ProgressContext_DirectUpload.svelte"
	import SceneUploadError from "./SceneUpload_Error.svelte"
	import { Toast } from "toaster"
	import { getTusUploadId, type UploadCompletion } from "file-uploader"

	interface Props {
		existingSceneId: number
		sceneName: string
		projectFolder: number
		selectedDeviceId: number
		onFail?: () => void
		postSubmit: (id: number) => void
		triggerCancel?: () => void
		triggerUpload?: () => void
	}

	let {
		existingSceneId,
		sceneName,
		projectFolder,
		selectedDeviceId,
		onFail,
		postSubmit,
		triggerCancel = $bindable(),
		triggerUpload = $bindable(),
	}: Props = $props()

	let uploadState: "upload" | "finalizing" | "processing" | "error" = $state("upload")
	let errorMessage: string = $state()
	let errorCode: number = $state()
	let entryId: number = $state()
	let progress: number = $state(0)

	function showError(code: number, message: string) {
		uploadState = "error"
		errorMessage = message
		errorCode = code
		Toast.error(message)
		console.error("Direct upload failed", code, message)
		onFail?.()
	}

	/**
	 * tusd has all the bytes. The API's post-finish hook creates (or targets) the project and copies
	 * the bytes into it; the tus upload id tusd gave us is the handle for asking the API which
	 * project that was and whether the copy is done, so processing starts on exactly this upload
	 * and never before its source object exists.
	 */
	async function postUpload(result: UploadCompletion) {
		const uploaded = result.successful?.[0]
		const tusId = getTusUploadId(uploaded)
		if (!tusId) {
			return showError(
				500,
				"The upload finished but tusd returned no upload id, so the project cannot be identified. Nothing was started."
			)
		}

		uploadState = "finalizing"
		Toast.success("Upload complete, finalizing on the server.")

		try {
			const ready = await waitForUploadReady(tusId, (id) => LuxedoRPC.api.upload.tus_upload_state(id), {
				sizeBytes: uploaded?.size,
			})
			entryId = ready.entry_id
		} catch (err) {
			return showError(500, err instanceof Error ? err.message : String(err))
		}

		if (existingSceneId && entryId !== existingSceneId) {
			return showError(
				500,
				`The server finalized the upload into project ${entryId}, not the project being replaced (${existingSceneId}). Nothing was started.`
			)
		}

		// Dispatch first, then show processing: flipping the state before the call left the modal
		// on a permanent 0% bar under a success toast whenever the dispatch failed (PR #1052 review).
		let dispatched: Awaited<ReturnType<typeof LuxedoRPC.api.scene.project_process_begin>>
		try {
			await DataHandlerScene.pull([entryId])
			dispatched = await LuxedoRPC.api.scene.project_process_begin(entryId)
		} catch (err) {
			const code = err instanceof RPCError ? err.statusCode : 500
			const message = err instanceof Error ? err.message : String(err)
			return showError(code, `The upload finished, but processing could not be started for project ${entryId}: ${message}`)
		}
		uploadState = "processing"
		if (dispatched && "duplicate" in dispatched && dispatched.duplicate) {
			// Nothing new was started: a job for this project is already queued or running (it may
			// have been started from another tab). Follow that one.
			Toast.text("Processing is already running for this project. Following it.")
		} else {
			Toast.success("Starting processing.")
		}
		// Unsubscribed on the outcome: the context is a module singleton, so a subscription left
		// behind fires again for the next upload of the same scene (review council, 2026-09-24).
		const callbackId = DirectUploadProgressContext.subscribe(entryId, {
			progress: (p) => {
				progress = p
			},
			fail: (code, message) => {
				DirectUploadProgressContext.unsubscribe(entryId, callbackId)
				uploadState = "error"
				errorMessage = message
				errorCode = code
				Toast.error("Processing failed...")
				console.error("Failed to process direct upload", code, message)
			},
			success: () => {
				DirectUploadProgressContext.unsubscribe(entryId, callbackId)
				Toast.success("Processing completed succesfully!")
				postSubmit(entryId)
			},
		})
		// Live gpq events reach only the session that started the job; the registry poll is the
		// session-independent source, and the only one when the dispatch was a duplicate.
		DirectUploadProgressContext.watch([entryId])
	}
</script>

<div class="flex-column scene-uploader {uploadState}">
	{#if uploadState === "upload"}
		<SceneUploadUpload
			{postUpload}
			onError={(error) => showError(error.code ?? 500, error.message)}
			{projectFolder}
			sceneId={existingSceneId}
			{sceneName}
			{selectedDeviceId}
			bind:triggerCancel
			bind:triggerUpload
		/>
	{:else if uploadState === "finalizing"}
		<SceneUploadProcess progress={0} finalizing />
	{:else if uploadState === "processing"}
		<SceneUploadProcess {progress} />
	{:else if uploadState === "error"}
		<SceneUploadError {errorMessage} {errorCode} />
	{/if}
</div>

<style>
	.scene-uploader :global(.file-uploader .uppy-Dashboard-inner) {
		background-color: var(--color-bg-dark-transparent);
	}

	.scene-uploader :global(.file-uploader .uppy-Dashboard-inner) {
		background-color: var(--color-bg-dark-transparent);
	}

	.scene-uploader :global(.file-uploader .uppy-Dashboard-AddFiles-title) {
		font-size: var(--h2);
	}

	.scene-uploader :global(.file-uploader .uppy-DashboardContent-bar) {
		background-color: var(--color-bg-dark-transparent);
	}

	.scene-uploader :global(.file-uploader .uppy-StatusBar-actions) {
		display: none;
	}

	:global(.isMobile) .scene-uploader :global(.upload-header) {
		display: none;
	}

	:global(.isMobile) .scene-uploader :global(.input-container) {
		width: 100%;
		justify-content: space-between;
	}

	:global(.isMobile) .scene-uploader :global(.file-uploader) {
		height: 100%;
	}
</style>
